import type { planningPreparationSchema } from 'features/libra/inspectionPlanning/planningPreparationConfig'
import {
  InspectionArea,
  niiAssessmentReferenceSchema,
  PlanningAreasConfig,
  planningStrategiesSchema,
  sourceZonesSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import {
  formatScope,
  scopeBaseUnit,
} from 'features/libra/inspectionPlanning/scopeMeasurement'
import { techniqueCatalogueSchema } from 'features/libra/inspectionPlanning/techniqueTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { formatReportFieldValue } from 'features/libra/pdf/reportFieldFormatting'
import {
  getPlannedInsulationRemoval,
  readInsulationPreparationRows,
} from 'features/libra/sections/insulationPreparationSection/insulationPreparationModel'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { parseInspectionRecommendations } from 'features/libra/stepComponents/niiReport/inspectionRecommendations'
import { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import { GeckoMeasurementSystem } from 'types'
import { z } from 'zod'

import { buildWorkPackChapters } from './buildWorkPackChapters'
import { buildWorkPackFormSections } from './buildWorkPackFormSections'
import { buildWorkPackZones } from './buildWorkPackZones'
import { buildWorkPackDrawings } from './workPackDrawings'
import {
  PlanningWorkPack,
  WorkPackConfig,
  WorkPackSection,
} from './workPackTypes'

export type WorkPackInputs = {
  execution?: z.infer<typeof planningPreparationSchema>
  documentNames?: Record<string, string>
  measurementSystem?: GeckoMeasurementSystem
  config: PlanningAreasConfig
  exportConfig: WorkPackConfig
  preparation: z.infer<typeof planningPreparationSchema>
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  areas: InspectionArea[]
  globalContext: LibraWorkflowGlobalContext
  ticketId: string
  version: number
  assetName: string
  siteName: string
  generatedAt: string
}

export function buildPlanningWorkPack(input: WorkPackInputs): PlanningWorkPack {
  const {
    fieldValues,
    preparation,
    config,
    areas,
    globalContext,
    exportConfig,
  } = input
  const strategies = planningStrategiesSchema.parse(
    globalContext.inspectionPlanningStrategies
  )
  const catalogue = techniqueCatalogueSchema.parse(
    globalContext[config.techniqueCatalogueContextKey]
  )
  const source = niiAssessmentReferenceSchema.parse(
    unwrapFieldValue(fieldValues[preparation.sourceField])
  )
  const zones = sourceZonesSchema.parse(
    unwrapFieldValue(fieldValues[config.sourceZonesField])
  )
  const recommendations = parseInspectionRecommendations({
    inspection_recommendations: fieldValues[config.recommendationsField],
  })
  const text = (field: string) =>
    formatReportFieldValue(
      unwrapFieldValue(fieldValues[field]),
      input.ontologyFields[field]
    )
  const visible = getVisibleSectionIds(preparation.form, fieldValues)
  const visibleSections = preparation.sections.filter((section) =>
    visible.includes(section.id)
  )
  const sections: WorkPackSection[] = [
    {
      title: 'Work-pack reference',
      rows: [
        {
          label: 'Asset / site',
          value: `${input.assetName} / ${input.siteName}`,
        },
        {
          label: 'Planning ticket / version',
          value: `${input.ticketId} / ${input.version}`,
        },
        { label: 'Generated (UTC)', value: input.generatedAt },
        { label: 'Completed source assessment', value: source.title },
        {
          label: 'Source ticket / version',
          value: `${source.ticketId} / ${source.version}`,
        },
        { label: 'Source workflow', value: source.workflowId },
        { label: 'Source captured (UTC)', value: source.capturedAt },
      ],
    },
    {
      title: 'Captured assessment basis',
      rows: [
        { label: 'Assessment summary', value: text(preparation.summaryField) },
        ...preparation.sourceDetails.map((detail) => ({
          label: detail.label,
          value: text(detail.field),
        })),
        ...recommendations.assetLevel.map((value) => ({
          label: 'Asset recommendation',
          value,
        })),
      ],
    },
    ...buildWorkPackFormSections(
      visibleSections,
      input.ontologyFields,
      fieldValues
    ),
  ]
  const areaText = (value: number | undefined) =>
    formatScope(
      value === undefined
        ? undefined
        : { num: value, unit: scopeBaseUnit('area').id },
      'area',
      input.measurementSystem ?? GeckoMeasurementSystem.Metric
    )
  for (const section of visibleSections) {
    if (section.type !== 'insulation_preparation') continue
    const preparation = readInsulationPreparationRows(
      section,
      fieldValues,
      globalContext
    )
    if (!preparation.success) throw preparation.error
    const insulation = preparation.data
    if (!insulation.length) continue
    sections.push({
      title: section.title,
      rows: [
        ...insulation.map((row) => ({
          label: row.title,
          value:
            row.insulated === false
              ? 'No insulation removal'
              : `Estimate: ${areaText(row.estimatedRemovalAreaM2)}; planned: ${areaText(getPlannedInsulationRemoval(row))}`,
        })),
        {
          label: 'Total planned removal',
          value: areaText(
            insulation.some(
              (row) => getPlannedInsulationRemoval(row) === undefined
            )
              ? undefined
              : insulation.reduce(
                  (sum, row) => sum + (getPlannedInsulationRemoval(row) ?? 0),
                  0
                )
          ),
        },
      ],
    })
  }
  const zoneSections = buildWorkPackZones(
    zones,
    areas,
    catalogue,
    strategies,
    recommendations,
    input.measurementSystem
  )
  const drawings = buildWorkPackDrawings(zones, areas, catalogue)
  return {
    title: exportConfig.title,
    purpose: exportConfig.purpose,
    ticketId: input.ticketId,
    version: input.version,
    generatedAt: input.generatedAt,
    filename:
      `${exportConfig.filenamePrefix}-${input.assetName}-${input.ticketId}-v${input.version}.pdf`.replace(
        /[^\p{L}\p{N}_.-]/gu,
        '-'
      ),
    cover: sections[0],
    contentsTitle: exportConfig.headings.contents,
    chapters: buildWorkPackChapters(input, sections, zoneSections, drawings),
    sections,
    zones: zoneSections,
    drawings,
  }
}
